<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { useDisplay } from 'vuetify';
import CollectionIcon from './CollectionIcon.vue';
import useMyCollections from '../use-my-collections';
import type { ContentCollection, ContentReference } from '@ilteducation/content-collections-types';
import useLocale from '../composables/useLocale';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import useFavourites from '../use-favourites';
import useCollectionName from '../use-collection-name';
import { isSameReference } from '../utils';

type Props = {
  contentReferences: ContentReference[];
  context: string;
  showIcons?: boolean;
  includeFavorites?: boolean; // whether to include the "Favorites" collection in the list or not
};

const props = withDefaults(defineProps<Props>(), {
  showIcons: true,
});

const emit = defineEmits<{
  (e: 'abort'): void;
  (e: 'open-create-collection'): void;
  (e: 'save', collection: ContentCollection | undefined): void;
}>();

const { t } = useLocale();
const { xs } = useDisplay();

const { collections: myCollections, isLoading } = useMyCollections(props.context, 500); // load "all" until we have proper pagination...
const { contentReferences } = toRefs(props);

const { collection: favouritesCollection } = useFavourites(ref({ context: props.context }));

const selectedCollection = ref<ContentCollection | undefined>(undefined);

const collections = computed(() => {
  return [...(props.includeFavorites ? [favouritesCollection.value] : []), ...myCollections.value];
});

/**
 * Returns an object where each key is the collection ID and the value is a boolean showing
 * whether all the selected content is in the collection or not.
 */
const contentInCollection = computed(() => {
  return collections.value.reduce<Record<string, boolean>>((accumulator, currentCollection) => {
    accumulator[currentCollection.id] = checkIfInCollection(currentCollection);
    return accumulator;
  }, {});
});

/**
 * Verifies that all the selected content are in a given collection.
 */
const checkIfInCollection = (collection: ContentCollection) => {
  let selContentInCollection = false;
  if (collection.contents.type === 'ContentsByReferences') {
    const collectionContent = collection.contents.references;
    selContentInCollection = contentReferences.value.every((ref) =>
      collectionContent.some((content) => isSameReference(content, ref)),
    );
  }
  return selContentInCollection;
};

const disableSave = computed(() => {
  if (selectedCollection?.value) {
    return contentInCollection.value[selectedCollection.value.id];
  }
  return !selectedCollection.value;
});

const { collectionName } = useCollectionName();
</script>

<template>
  <v-card-text class="pb-0 pt-0 collection-list-container">
    <div v-if="isLoading" class="d-flex justify-center">
      <v-progress-linear indeterminate :size="40" :width="3" color="accent" />
    </div>
    <v-list
      class="overflow-y-auto"
      :aria-label="t('content_collections.label_choose_collection')"
      bg-color="transparent"
    >
      <v-item-group v-model="selectedCollection" class="ga-2 d-flex flex-column">
        <v-item
          v-for="collection in collections"
          :key="collection.id"
          v-slot="{ isSelected, toggle }"
          :value="collection"
        >
          <v-list-item
            :active="isSelected"
            class="rounded"
            @click="toggle"
            role="option"
            :aria-selected="isSelected"
            :disabled="contentInCollection[collection.id]"
          >
            <template v-if="showIcons" #prepend>
              <CollectionIcon class="compact-list-item-icon mr-4" :symbol="collection.icon" />
            </template>
            <div class="shelf-title">
              <v-list-item-title>{{ collectionName(collection.name) }}</v-list-item-title>
            </div>
            <template #append>
              <FontAwesomeIcon v-if="contentInCollection[collection.id]" icon="fa-regular fa-folder-check" />
              <FontAwesomeIcon v-if="isSelected" icon="fa-solid fa-check" />
            </template>
          </v-list-item>
        </v-item>
      </v-item-group>
    </v-list>
  </v-card-text>
  <v-card-actions class="pa-6 d-flex" :class="xs ? 'flex-wrap' : ''">
    <v-btn
      :class="xs ? 'w-100' : 'px-4'"
      class="text-none"
      color="primary"
      variant="text"
      :aria-label="t('content_collections.label_new_collection')"
      @click="emit('open-create-collection')"
    >
      <template #prepend>
        <FontAwesomeIcon icon="fa-light fa-plus" />
      </template>
      {{ t('content_collections.label_new_collection') }}
    </v-btn>
    <v-spacer v-if="!xs"></v-spacer>
    <div class="d-flex ga-4" :class="xs ? 'flex-wrap w-100' : ''">
      <v-btn
        min-width="120"
        :class="xs ? '' : 'px-4'"
        variant="outlined"
        color="secondary"
        block
        @click="emit('abort')"
      >
        <template #append>
          <FontAwesomeIcon icon="fa-solid fa-xmark" />
        </template>
        <span class="text-none">{{ t('button_cancel') }}</span>
      </v-btn>
      <v-btn
        min-width="120"
        :color="disableSave ? 'overline-variant' : 'primary'"
        variant="flat"
        block
        :disabled="disableSave"
        :loading="isLoading"
        @click="emit('save', selectedCollection)"
      >
        <template #append>
          <FontAwesomeIcon icon="fa-solid fa-check" />
        </template>
        <span class="text-none">{{ t('button_save') }}</span>
      </v-btn>
    </div>
  </v-card-actions>
</template>

<style scoped lang="scss">
.collection-list-container {
  overflow-y: auto;
}
</style>
